import { EditorView, NodeView } from 'prosemirror-view'

import Styles from './OrderedList.module.scss'
import { Node } from 'prosemirror-model'
import { MAX_LIST_DEPTH, StrongNode, ExtractStrongNodeSpecAttributes, COUNTER_STYLE, nodes } from 'prose-core'
import { times } from 'lodash'

const updateOrderedListEventName = 'prose:nodeview:ordered_list:updateDomNode'

function getCounterValues(node: StrongNode<typeof nodes.ordered_list>, listStartOffset?: number | number[]) {
  const _listStartOffset = listStartOffset ?? node.attrs.listStartOffset
  const counterValues = times(MAX_LIST_DEPTH, (i) => {
    if (Array.isArray(_listStartOffset)) {
      return _listStartOffset[i]
    } else if (i === 0) {
      return _listStartOffset
    } else {
      return 1
    }
  })
  let lastDepth = 0
  node.descendants((n, p) => {
    const descendant = n as StrongNode<typeof nodes.list_item>
    const descendant_depth = descendant.attrs.depth
    counterValues[descendant_depth]++
    if (descendant_depth < lastDepth) {
      // we've gone up a level, so reset all the counters below this level
      for (let i = descendant_depth + 1; i <= MAX_LIST_DEPTH; i++) {
        counterValues[i] = 1
      }
    }
    lastDepth = descendant_depth
    return false
  })
  return counterValues
}

export class OrderedList implements NodeView {
  contentDOM: HTMLOListElement
  dom: HTMLOListElement
  getPos: () => number
  view: EditorView
  node: Node

  constructor(node: Node, view: EditorView, getPos: () => number) {
    this.getPos = getPos
    this.view = view
    this.node = node

    document.body.addEventListener(updateOrderedListEventName, this.handleUpdateOrderedList)
    this.contentDOM = this.dom = this.createDomNode(node as any as StrongNode<typeof nodes.ordered_list>)
    this.handleUpdateOrderedList() // initial update, so that new nodes spawn with the correct numbering
  }

  destroy() {
    document.body.removeEventListener(updateOrderedListEventName, () => this.handleUpdateOrderedList())
  }

  private handleUpdateOrderedList = () => {
    const _listStartOffset = (this.node.attrs as ExtractStrongNodeSpecAttributes<typeof nodes.ordered_list>)
      .listStartOffset
    const listStartOffset = Array.isArray(_listStartOffset) ? _listStartOffset : [_listStartOffset]
    if (listStartOffset.some((x) => x > 1)) {
      listStartOffset.forEach((x, i) => {
        this.dom.style.setProperty(`--ol-start-level${i}`, x.toString())
      })
      // skip looking for counters in previous nodes, we have a set counter value
      return
    }
    const ownPos = (() => {
      let myPos: number | undefined = undefined
      this.view.state.doc.descendants((n, p) => {
        if (myPos !== undefined) return false // stop traversing
        if (n === this.node) myPos = p
        return true
      })
      return myPos
    })()
    if (ownPos == null) return // couldn't find self in doc
    const $ownPos = this.view.state.doc.resolve(ownPos)
    const ownIndex = $ownPos.index()
    const parent = $ownPos.parent
    const ownFirstChildDepth = (this.node.firstChild as StrongNode<typeof nodes.list_item>)?.attrs.depth
    let foundCounters: number[] = Array(MAX_LIST_DEPTH).fill(1)
    for (let i = ownIndex - 1; i >= 0; i--) {
      /**
       * traverse backwards, skipping over any list nodes which are not ordered_list, collecting the
       * counter values of all the ordered_list nodes we encounter.
       * We stop when we encounter:
       *  - an ordered_list node with a set counter value
       *  - a non-list node
       *  - the start of the document
       */
      const sibling = parent.child(i)
      if (!sibling.type.groups.includes('list')) break
      if (sibling.type === this.view.state.schema.nodes.ordered_list) {
        foundCounters = getCounterValues(sibling as StrongNode<typeof nodes.ordered_list>, foundCounters)
        const siblingListStartOffset = (sibling.attrs as ExtractStrongNodeSpecAttributes<typeof nodes.ordered_list>)
          .listStartOffset
        if (Array.isArray(siblingListStartOffset) ? siblingListStartOffset[0] > 1 : siblingListStartOffset > 1) break // stop if we encounter a list with a set counter value
      }
    }
    if (foundCounters.length === 0) return // no counters found
    const actualCounters = times(ownFirstChildDepth + 1, (i) => {
      // if the first child is indented, we treat lower depths as though they were one level lower
      if (i === ownFirstChildDepth) {
        // if we're at the first child depth
        const levelCounter = foundCounters[i] ?? 1
        // if the child is indented (it is, since we're here) and we're setting the counter for
        // not-the-first-level, or if the counter value is greater than 1, we need to set the counter
        if (ownFirstChildDepth > 0 || levelCounter > 1) {
          return levelCounter
        }
        return null
      } else {
        // if we're at a lower depth
        return foundCounters[i] - 1
      }
    })
    times(MAX_LIST_DEPTH, (i) => {
      const x = actualCounters[i] ?? null
      if (x !== null) {
        this.dom.style.setProperty(`--ol-start-level${i}`, x.toString())
      } else if (this.dom.style.getPropertyValue(`--ol-start-level${i}`) !== '') {
        this.dom.style.removeProperty(`--ol-start-level${i}`)
      }
    })
  }

  update(node: Node) {
    if (node.type === this.view.state.schema.nodes.ordered_list) {
      this.updateDomNode(this.dom, node as any as StrongNode<typeof nodes.ordered_list>)

      return true
    }

    return false
  }

  private createDomNode(node: StrongNode<typeof nodes.ordered_list>) {
    const ol = document.createElement('ol')
    ol.dataset.type = 'ordered_list'

    this.updateDomNode(ol, node)

    return ol
  }

  private updateDomNode(domNode: HTMLOListElement, node: StrongNode<typeof nodes.ordered_list>) {
    if (node !== this.node) {
      this.node = node
      document.body.dispatchEvent(new CustomEvent(updateOrderedListEventName))
    }
    domNode.classList.add(Styles.orderedList)
    domNode.dataset.flattenmarker = node.attrs.flattenMarker.toString()

    const styleProperties = this.buildStyleProperties(node.attrs.counterStyles, node.attrs.counterSeparator)
    for (let propertyKey in styleProperties) domNode.style.setProperty(propertyKey, styleProperties[propertyKey])
  }

  private buildStyleProperties(counterStyles: COUNTER_STYLE[], counterSeparator: string) {
    const styleProperties: { [variableName: string]: string } = {}

    for (let i = 0; i < MAX_LIST_DEPTH; i++) {
      styleProperties[`--ol-counter-style-depth${i}`] = counterStyles[i % counterStyles.length]
    }

    styleProperties['--ol-separator'] = `"${counterSeparator}"`

    return styleProperties
  }
}
